import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowDown, Check } from "lucide-react";
import { BookingDialog } from "@/components/site/BookingDialog";
import { IMAGES } from "@/lib/content";

const bullets = [
  "Genuine front-office banking pedigree",
  "Dual-market Monaco — Geneva intelligence",
  "The full talent lifecycle, one partner",
];

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <section ref={ref} className="relative overflow-hidden hero-noise" data-testid="hero-section">
      <div className="container-bp grid min-h-[100svh] grid-cols-1 items-center gap-10 pb-16 pt-28 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:pb-20 lg:pt-28">
        {/* Left: copy */}
        <motion.div
          initial={reduce ? false : "hidden"}
          animate={reduce ? {} : "show"}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } } }}
          className="order-2 lg:order-1"
        >
          <motion.p
            variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } } }}
            className="eyebrow inline-flex items-center gap-2 text-[color:var(--bp-teal)]"
          >
            <span className="h-px w-8 bg-[color:var(--bp-teal)]" /> Executive Search &amp; Talent Intelligence
          </motion.p>
          <motion.h1
            variants={{ hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } } }}
            className="mt-6 max-w-xl font-display text-[2.4rem] font-semibold leading-[1.06] tracking-[-0.02em] text-[color:var(--bp-cream)] sm:text-[3.1rem] lg:text-[3.6rem]"
          >
            The only banker-founded talent partner for{" "}
            <span className="text-[color:var(--bp-teal)]">Monaco &amp; Geneva</span> private banking.
          </motion.h1>
          <motion.p
            variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } } }}
            className="mt-6 max-w-lg text-[15px] leading-[1.75] text-[color:var(--bp-text-muted)] sm:text-base"
          >
            From strategic hire to leadership development — we cover the full talent lifecycle because we&apos;ve sat on your side of the table.
          </motion.p>

          <motion.div
            variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } } }}
            className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <BookingDialog
              trigger={
                <button data-testid="hero-book-briefing-button" className="group inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--bp-teal)] px-7 py-4 text-sm font-semibold text-[color:var(--bp-bg)] shadow-[var(--shadow-soft)] transition-shadow hover:shadow-[var(--shadow-lift)]">
                  Book a Confidential Briefing
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              }
            />
            <Link
              to="/services"
              data-testid="hero-explore-services-link"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[color:var(--bp-border)] px-7 py-4 text-sm font-medium text-[color:var(--bp-cream)] transition-colors hover:border-[color:var(--bp-teal)]"
            >
              Explore Our Services
            </Link>
          </motion.div>

          <motion.ul
            variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } } }}
            className="mt-10 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-x-6"
          >
            {bullets.map((b) => (
              <li key={b} className="flex items-center gap-2 text-[13px] text-[color:var(--bp-text-muted)]">
                <Check className="h-4 w-4 shrink-0 text-[color:var(--bp-teal)]" /> {b}
              </li>
            ))}
          </motion.ul>
          <motion.p
            variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 1 } } }}
            className="mt-8 text-[11px] uppercase tracking-[0.22em] text-[color:var(--bp-text-muted)]/70"
          >
            Monaco · Geneva · Paris
          </motion.p>
        </motion.div>

        {/* Right: team image */}
        <motion.div
          initial={reduce ? false : { opacity: 0, scale: 0.96 }}
          animate={reduce ? {} : { opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          className="relative order-1 lg:order-2"
        >
          <div className="relative aspect-[4/5] overflow-hidden rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] shadow-[var(--shadow-lift)] sm:aspect-[5/5] lg:aspect-[4/5]">
            <motion.div style={reduce ? {} : { y: imgY }} className="absolute inset-0 h-[118%] w-full">
              <img
                src={IMAGES.heroTeam}
                alt="The Beyond Potentials team in their Geneva private-banking advisory office"
                className={`h-full w-full object-cover object-center ${reduce ? "" : "hero-kenburns"}`}
                loading="eager"
                fetchpriority="high"
              />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--bp-bg)] via-[color:var(--bp-bg)]/15 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--bp-bg)]/35 to-transparent lg:hidden" />
            <div className="absolute left-5 top-5 h-10 w-10 border-l border-t border-[color:var(--bp-gold)]/70" />
            <div className="absolute bottom-5 right-5 h-10 w-10 border-b border-r border-[color:var(--bp-gold)]/70" />
          </div>
          {/* Floating stat card */}
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={reduce ? {} : { opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="absolute -left-3 bottom-8 w-44 rounded-[var(--radius-md)] border border-[color:var(--bp-border)] bg-[color:rgba(13,43,77,0.94)] p-4 backdrop-blur-md sm:-left-6"
          >
            <p className="font-display text-3xl font-semibold text-[color:var(--bp-gold)]">85%</p>
            <p className="mt-1 text-[12px] leading-snug text-[color:var(--bp-text-muted)]">of our placements were passive candidates</p>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex">
        <span className="text-[10px] uppercase tracking-[0.24em] text-[color:var(--bp-text-muted)]/60">Scroll</span>
        <motion.span animate={reduce ? {} : { y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown className="h-4 w-4 text-[color:var(--bp-teal)]" />
        </motion.span>
      </div>
    </section>
  );
};
